import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  AfterViewInit,
  ViewChild,
  ElementRef,
  signal,
  effect,
  ChangeDetectionStrategy,
  TemplateRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { trigger, transition, style, animate } from '@angular/animations';
import { IconButtonComponent } from 'src/app/ui-kit/icon-button/icon-button.component';

/**
 * Command Palette Component
 *
 * A reusable command palette component with search functionality.
 *
 * Features:
 * - Auto-focusing search input when opened
 * - Global backdrop integration
 * - Content projection for custom results
 * - Signal-based search value exposure
 * - Smooth animations
 *
 * @example
 * <app-command-palette
 *   [isOpen]="commandPaletteOpen()"
 *   (close)="closeCommandPalette()"
 * >
 *   <div class="command-palette-content">
 *     <!-- Your custom content here -->
 *   </div>
 * </app-command-palette>
 *
 * // Access search value
 * @ViewChild(CommandPaletteComponent) commandPalette!: CommandPaletteComponent;
 * effect(() => {
 *   console.log('Search:', this.commandPalette.searchValue());
 * });
 */
@Component({
  selector: 'app-command-palette',
  standalone: true,
  imports: [CommonModule, FormsModule, IconButtonComponent],
  templateUrl: './command-palette.component.html',
  styleUrls: ['./command-palette.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('palette', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(-20px) scale(0.95)' }),
        animate(
          '200ms cubic-bezier(0.16, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'translateY(0) scale(1)' })
        ),
      ]),
      transition(':leave', [
        animate(
          '150ms ease-in',
          style({ opacity: 0, transform: 'translateY(-20px) scale(0.95)' })
        ),
      ]),
    ]),
  ],
})
export class CommandPaletteComponent implements OnChanges, AfterViewInit {
  /**
   * Whether the command palette is open
   */
  @Input() isOpen = false;

  /**
   * Placeholder text for the search input
   */
  @Input() placeholder = 'Type a command or search...';

  /**
   * Event emitted when the palette should close
   */
  @Output() close = new EventEmitter<void>();

  /**
   * Event emitted when the search value changes
   */
  @Output() searchChange = new EventEmitter<string>();

  /**
   * Content template to display in the palette
   */
  @Input() contentTemplate: TemplateRef<any> | null = null;

  /**
   * Search input element reference
   */
  @ViewChild('searchInput') searchInput?: ElementRef<HTMLInputElement>;

  /**
   * Signal for the current search value
   * Consumer components can read this to filter results
   */
  searchValue = signal<string>('');

  /**
   * Internal model for the search input
   */
  searchModel = '';

  constructor() {
    // Effect to update signal when model changes
    effect(() => {
      this.searchValue.set(this.searchModel);
    }, { allowSignalWrites: true });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isOpen']) {
      if (this.isOpen) {
        // Clear search when opening
        this.searchModel = '';
        this.searchValue.set('');
        
        // Focus the input after the view is rendered
        this.focusSearchInput();
      }
    }
  }

  ngAfterViewInit(): void {
    // Auto-focus the search input when opened
    if (this.isOpen) {
      this.focusSearchInput();
    }
  }

  /**
   * Handle search input changes
   */
  onSearchChange(value: string): void {
    this.searchModel = value;
    this.searchValue.set(value);
    this.searchChange.emit(value);
  }

  /**
   * Focus the search input
   */
  private focusSearchInput(): void {
    // Use a longer timeout to ensure the DOM is fully rendered
    // This is especially important since the component uses @if which
    // completely removes/adds the element from the DOM
    setTimeout(() => {
      this.searchInput?.nativeElement.focus();
    }, 0);
  }

  /**
   * Handle palette close
   */
  onClose(): void {
    this.close.emit();
  }

  /**
   * Handle backdrop click
   */
  onBackdropClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.onClose();
  }

  /**
   * Handle escape key
   */
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.onClose();
    }
  }

  /**
   * Handle keydown on search input
   * Close palette when backspace is pressed on empty field
   * Focus first result when down arrow is pressed
   */
  onSearchKeyDown(event: KeyboardEvent): void {
    // Close on backspace when field is empty
    if (event.key === 'Backspace' && this.searchModel === '') {
      this.onClose();
      return;
    }

    // Focus first result on down arrow
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      this.focusFirstResult();
      return;
    }
  }

  /**
   * Focus the first result item in the content area
   */
  private focusFirstResult(): void {
    // Find the first focusable button in the command palette content
    const contentElement = document.querySelector('.command-palette-content');
    if (contentElement) {
      const firstButton = contentElement.querySelector('button') as HTMLElement;
      if (firstButton) {
        firstButton.focus();
      }
    }
  }

  /**
   * Prevent palette clicks from closing it
   */
  onPaletteClick(event: Event): void {
    event.stopPropagation();
  }
}
